/**
 * ÉLORA Theme JavaScript
 * Luxury Pilates & Wellness — Shopify Theme
 * Modules: Announcement, Hero, Header, Cart, Search, Popup, Challenge, Kit, Quick-Add
 */

(function () {
  'use strict';

  /* ─── Utilities ─── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const debounce = (fn, ms = 200) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  function formatMoney(cents) {
    const fmt = window.ELORA?.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/, amount)
              .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
              .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }

  function showToast(message, duration = 3000) {
    const container = document.getElementById('toast-container');
    if (!container) return;
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    container.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('toast--visible'));
    setTimeout(() => {
      toast.classList.remove('toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  /* ─── Announcement Bar ─── */
  function initAnnouncement() {
    const bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    const msgs = bar.querySelectorAll('.announcement-bar__message');
    if (msgs.length < 2) return;
    let idx = 0;
    const speed = parseInt(bar.dataset.speed || '4000', 10);

    setInterval(function() {
      msgs[idx].style.opacity = '0';
      msgs[idx].classList.remove('is-active');
      idx = (idx + 1) % msgs.length;
      msgs[idx].style.opacity = '1';
      msgs[idx].classList.add('is-active');
    }, speed);
  }

  /* ─── Hero Slider ─── */
  function initHero() {
    const hero = $('[data-hero-slider]');
    if (!hero) return;
    const slides = $$('.hero__slide', hero);
    const dots = $$('.hero__dot', hero);
    const prevBtn = $('[data-hero-prev]', hero);
    const nextBtn = $('[data-hero-next]', hero);
    if (slides.length < 2) return;

    let current = 0;
    let timer;
    const speed = parseInt(hero.dataset.speed || '5000', 10);
    const auto = hero.dataset.autoplay !== 'false';

    function goTo(i) {
      slides[current].classList.remove('is-active');
      if (dots[current]) dots[current].classList.remove('is-active');
      current = (i + slides.length) % slides.length;
      slides[current].classList.add('is-active');
      if (dots[current]) dots[current].classList.add('is-active');
    }

    function startAuto() { if (auto) timer = setInterval(() => goTo(current + 1), speed); }
    function stopAuto() { clearInterval(timer); }

    on(prevBtn, 'click', () => { stopAuto(); goTo(current - 1); startAuto(); });
    on(nextBtn, 'click', () => { stopAuto(); goTo(current + 1); startAuto(); });
    dots.forEach((d, i) => on(d, 'click', () => { stopAuto(); goTo(i); startAuto(); }));

    // Swipe support
    let touchStartX = 0;
    on(hero, 'touchstart', e => { touchStartX = e.changedTouches[0].screenX; }, { passive: true });
    on(hero, 'touchend', e => {
      const diff = touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > 50) { stopAuto(); goTo(current + (diff > 0 ? 1 : -1)); startAuto(); }
    }, { passive: true });

    startAuto();
  }

  /* ─── Sticky Header ─── */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    let lastScroll = 0;

    window.addEventListener('scroll', () => {
      const sy = window.scrollY;
      header.classList.toggle('scrolled', sy > 60);
      // header stays visible (sticky)
      lastScroll = sy;
    }, { passive: true });

    // Mobile menu
    const burger = $('[data-mobile-open]', header);
    const mobileMenu = $('[data-mobile-menu]');
    const mobileClose = $('[data-mobile-close]');
    // overlay same as mobileMenu (fullscreen)

    function openMobile() {
      mobileMenu && mobileMenu.classList.add('is-open');
      // overlay handled by mobileMenu
      document.body.style.overflow = 'hidden';
    }
    function closeMobile() {
      mobileMenu && mobileMenu.classList.remove('is-open');
      // overlay handled by mobileMenu
      document.body.style.overflow = '';
    }

    on(burger, 'click', openMobile);
    on(mobileClose, 'click', closeMobile);
    // Close on click outside handled by closeMobile

    // Mobile accordion submenus
    $$('[data-mobile-menu] [data-submenu-toggle]').forEach(btn => {
      on(btn, 'click', () => {
        const sub = btn.nextElementSibling;
        const isOpen = btn.classList.contains('open');
        btn.classList.toggle('open', !isOpen);
        if (sub) sub.style.maxHeight = isOpen ? '0' : sub.scrollHeight + 'px';
      });
    });

    // Language toggle handled natively by form submit buttons
  }

  /* ─── Cart Drawer ─── */
  function initCartDrawer() {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;
    const overlay = $('[data-cart-overlay]');
    const closeBtn = $('.cart-drawer__close', drawer);

    window.openCartDrawer = function () {
      drawer.classList.add('is-open'); $('[data-cart-overlay]')?.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      refreshCartDrawer();
    };
    window.closeCartDrawer = function () {
      drawer.classList.remove('is-open'); $('[data-cart-overlay]')?.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    on(overlay, 'click', window.closeCartDrawer);
    on(closeBtn, 'click', window.closeCartDrawer);

    // ESC to close
    on(document, 'keydown', e => { if (e.key === 'Escape') window.closeCartDrawer(); });

    // Cart icon click
    $$('[data-cart-open]').forEach(el => on(el, 'click', e => {
      e.preventDefault();
      window.openCartDrawer();
    }));
  }

  async function refreshCartDrawer() {
    try {
      const res = await fetch(window.ELORA?.routes?.cart_url + '.js');
      const cart = await res.json();
      renderCartDrawer(cart);
      updateCartCount(cart.item_count);
    } catch (e) { console.error('Cart fetch error', e); }
  }

  function renderCartDrawer(cart) {
    const itemsEl = $('.cart-drawer__items');
    const subtotalEl = $('[data-cart-total]');
    const emptyEl = $('.cart-drawer__empty');
    const footerEl = $('[data-cart-drawer] .cart-drawer__footer');

    if (!itemsEl) return;

    if (cart.item_count === 0) {
      if (emptyEl) emptyEl.style.display = 'block';
      itemsEl.innerHTML = '<div class="cart-drawer__empty">' + (window.ELORA?.strings?.cartEmpty || 'Your cart is empty') + '</div>';
      if (footerEl) footerEl.style.display = 'none';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    if (footerEl) footerEl.style.display = '';

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="cart-drawer__item" data-key="${item.key}">
        <div class="cart-drawer__item-image">
          ${item.image ? `<img src="${item.image}" alt="${item.title}" width="80" height="80" loading="lazy">` : '<div class="cart-drawer__item-placeholder"></div>'}
        </div>
        <div class="cart-drawer__item-info">
          <p class="cart-drawer__item-title">${item.product_title}</p>
          ${item.variant_title ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
          <div class="cart-drawer__item-bottom">
            <div class="cart-drawer__qty">
              <button class="cart-drawer__qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease">−</button>
              <span class="cart-drawer__qty-value">${item.quantity}</span>
              <button class="cart-drawer__qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase">+</button>
            </div>
            <p class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</p>
          </div>
        </div>
        <button class="cart-drawer__item-remove" data-key="${item.key}" aria-label="Remove">&times;</button>
      </div>
    `).join('');

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Wire qty and remove
    $$('.cart-drawer__qty-btn', itemsEl).forEach(btn => on(btn, 'click', async () => {
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const item = cart.items.find(i => i.key === key);
      if (!item) return;
      const qty = action === 'increase' ? item.quantity + 1 : item.quantity - 1;
      await updateCartItem(key, Math.max(0, qty));
    }));

    $$('.cart-drawer__item-remove', itemsEl).forEach(btn => on(btn, 'click', () => updateCartItem(btn.dataset.key, 0)));
  }

  async function updateCartItem(key, qty) {
    try {
      await fetch(window.ELORA?.routes?.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      refreshCartDrawer();
    } catch (e) { console.error('Cart update error', e); }
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  async function addToCart(variantId, qty = 1) {
    try {
      const res = await fetch(window.ELORA?.routes?.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      showToast(window.ELORA?.strings?.added || 'Added to cart');
      if (window.ELORA?.cartType === 'drawer') window.openCartDrawer();
      else refreshCartDrawer();
    } catch (e) {
      console.error('Add to cart error', e);
      showToast('Error adding to cart');
    }
  }

  /* ─── Quick Add Buttons ─── */
  function initQuickAdd() {
    on(document, 'click', e => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const vid = btn.dataset.variantId || btn.dataset.quickAdd;
      if (!vid) return;
      btn.disabled = true;
      btn.textContent = '...';
      addToCart(parseInt(vid, 10)).finally(() => {
        btn.disabled = false;
        btn.textContent = window.ELORA?.strings?.addToCart || 'Add to Cart';
      });
    });
  }

  /* ─── Search Modal ─── */
  function initSearch() {
    const modal = $('[data-search-modal]');
    if (!modal) return;
    const closeBtn = $('[data-search-close]', modal);
    const input = $('[data-search-input]', modal);
    const results = $('[data-search-results]', modal);
    const popular = $('[data-search-popular]', modal);
    let debounceTimer;

    function openSearch() {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input && input.focus(), 200);
    }
    function closeSearch() {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      if (input) input.value = '';
      if (results) { results.style.display = 'none'; results.innerHTML = ''; }
      if (popular) popular.style.display = '';
    }

    $$('[data-search-open]').forEach(el => on(el, 'click', e => { e.preventDefault(); openSearch(); }));
    on(closeBtn, 'click', closeSearch);
    on(document, 'keydown', e => { if (e.key === 'Escape') closeSearch(); });

    // Popular tag clicks
    $$('[data-search-tag]', modal).forEach(tag => {
      on(tag, 'click', () => {
        if (input) {
          input.value = tag.dataset.searchTag || tag.textContent.trim();
          input.dispatchEvent(new Event('input'));
        }
      });
    });

    // Predictive search
    if (input && results) {
      on(input, 'input', () => {
        clearTimeout(debounceTimer);
        const q = input.value.trim();
        if (q.length < 2) {
          results.style.display = 'none';
          results.innerHTML = '';
          if (popular) popular.style.display = '';
          return;
        }
        debounceTimer = setTimeout(() => fetchPredictive(q), 300);
      });
    }

    function fetchPredictive(query) {
      var url = (ELORA?.routes?.predictive_search || '/search/suggest') + '?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6';
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(r => r.json())
        .then(data => {
          const products = data.resources && data.resources.results && data.resources.results.products || [];
          if (popular) popular.style.display = 'none';
          if (products.length === 0) {
            results.innerHTML = '<div class="search-modal__noresults">' + (ELORA?.strings?.noResults || 'No results found') + '</div>';
          } else {
            results.innerHTML = products.map(p => {
              const img = p.image ? '<img class="search-modal__result-img" src="' + p.image + '" alt="" loading="lazy">' : '<div class="search-modal__result-img"></div>';
              return '<a href="' + p.url + '" class="search-modal__result">' +
                img +
                '<div><div class="search-modal__result-title">' + p.title + '</div>' +
                '<div class="search-modal__result-type">' + p.type + '</div></div>' //+
                //'<span class="search-modal__result-price">' + formatMoney(p.price*100) + '</span></a>';
            }).join('');
          }
          results.style.display = '';
        })
        .catch(() => { results.style.display = 'none'; });
    }
  }

  /* ─── Email Popup ─── */
  function initEmailPopup() {
    const popup = $('.email-popup');
    if (!popup) return;
    const closeBtn = $('.email-popup__close', popup);
    const overlay = $('.email-popup__dismiss', popup);
    const form = $('form', popup);
    const delay = parseInt(popup.dataset.delay || '5000', 10);

    // Don't show if already dismissed
    const dismissed = localStorage.getItem('elora_popup_dismissed');
    if (dismissed) return;

    setTimeout(() => popup.classList.add('is-open'), delay);

    function closePopup() {
      popup.classList.remove('is-open');
      localStorage.setItem('elora_popup_dismissed', '1');
    }

    on(closeBtn, 'click', closePopup);
    on(overlay, 'click', closePopup);

    // Click background to close
    on(popup, 'click', e => { if (e.target === popup) closePopup(); });

    if (form) {
      on(form, 'submit', e => {
        // Allow default Shopify newsletter form submission
        localStorage.setItem('elora_popup_dismissed', '1');
      });
    }
  }

  /* ─── 10-Day Challenge ─── */
  function initChallenge() {
    const section = document.querySelector('.challenge');
    if (!section) return;

    const dayCards = $$('.challenge__day', section);
    const modal = document.querySelector('[data-day-modal]');
    const modalClose = document.querySelector('[data-day-modal-close]');
    const spinModal = document.querySelector('[data-spin-modal]');
    const progressBar = document.querySelector('[data-challenge-progress-bar]');
    const progressText = document.querySelector('[data-challenge-progress-text]');
    const progressPct = document.querySelector('[data-challenge-progress-pct]');

    const isPreview = section.dataset.preview === 'true';
    const isLoggedIn = section.dataset.customer === 'true';
    const loginUrl = section.dataset.loginUrl || '/account/login';
    const strings = window.ELORA_CHALLENGE?.strings || {};

    // Modal helpers
    function openModal(el) {
      if (!el) return;
      el.style.opacity = '1'; el.style.visibility = 'visible';
      document.body.style.overflow = 'hidden';
    }
    function closeModal(el) {
      if (!el) return;
      el.style.opacity = '0'; el.style.visibility = 'hidden';
      document.body.style.overflow = '';
    }

    // Progress
    var progress = JSON.parse(localStorage.getItem('elora_challenge') || '{}');
    if (!Array.isArray(progress.completed)) progress.completed = [];

    // SVG icons for modal
    var dayIcons = {
      1: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="18" cy="12" r="5"/><path d="M18 17v13M14 24h8"/></svg>',
      2: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M8 18h20M8 18l-2-6h4M28 18l2-6h-4M13 18v8M23 18v8"/></svg>',
      3: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 7C12 7 8 12 8 18s4 11 10 11 10-5 10-11S24 7 18 7z"/><path d="M18 13v10M13 18h10"/></svg>',
      4: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 7l2.5 7h7.5l-6 4.5 2.3 7L18 21l-6.3 4.5 2.3-7-6-4.5h7.5z"/></svg>',
      5: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><ellipse cx="18" cy="16" rx="12" ry="6"/><path d="M6 16v6c0 3.3 5.4 6 12 6s12-2.7 12-6v-6"/></svg>',
      6: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 6c-3 0-6 2.7-6 6s6 9 6 9 6-6 6-9-3-6-6-6z"/><path d="M12 24c2-1 4-1.5 6-1.5s4 .5 6 1.5"/></svg>',
      7: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="10" y="8" width="16" height="20" rx="3"/><path d="M14 14h8M14 18h8M14 22h5"/></svg>',
      8: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="18" cy="18" r="10"/><path d="M13 18c0-2.8 2.2-5 5-5M23 18c0 2.8-2.2 5-5 5"/></svg>',
      9: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M12 8v20M24 8v20M8 14h20M8 22h20"/></svg>',
      10: '<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 6l-10 7v2l10 5 10-5v-2z"/><path d="M8 15v7l10 7 10-7v-7"/></svg>'
    };

    function saveProgress() {
      localStorage.setItem('elora_challenge', JSON.stringify(progress));
    }

    function updateUI() {
      var count = progress.completed.length;

      // Progress bar
      if (progressBar) progressBar.style.width = (count * 10) + '%';
      if (progressText) progressText.textContent = count + ' / 10';
      if (progressPct) progressPct.textContent = (count * 10) + '%';

      // Day cards
      dayCards.forEach(function(card) {
        var day = parseInt(card.dataset.day, 10);
        var check = card.querySelector('.challenge__check');
        var lock = card.querySelector('.challenge__lock');
        var icon = card.querySelector('.challenge__icon');
        var isDone = progress.completed.indexOf(day) !== -1;

        if (isDone) {
          card.style.borderColor = 'var(--color-primary)';
          card.style.background = 'var(--color-offwhite)';
          if (check) check.style.display = 'block';
          if (lock) lock.style.display = 'none';
          if (icon) { icon.style.opacity = '1'; icon.style.color = 'var(--color-gold)'; }
        } else {
          card.style.borderColor = 'var(--color-light)';
          card.style.background = 'white';
          if (check) check.style.display = 'none';
          if (icon) { icon.style.opacity = '0.65'; icon.style.color = 'var(--color-primary)'; }
          // Show lock on days that need login (day 2+ for non-logged, non-preview)
          if (lock) {
            lock.style.display = (!isPreview && !isLoggedIn && day > 1) ? 'block' : 'none';
          }
        }
      });

      // Letters: E(1-2) L(3-4) O(5-6) R(7-8) A(9-10)
      var letters = $$('.challenge__letter', section);
      var map = [0, 0, 1, 1, 2, 2, 3, 3, 4, 4];
      letters.forEach(function(l) { l.style.color = 'var(--color-light)'; l.style.textShadow = 'none'; });
      progress.completed.forEach(function(d) {
        var li = map[d - 1];
        if (li !== undefined && letters[li]) {
          letters[li].style.color = 'var(--color-gold)';
          letters[li].style.textShadow = '0 0 24px rgba(201,169,110,0.35)';
        }
      });
    }

    updateUI();

    // Day card click
    dayCards.forEach(function(card) {
      on(card, 'click', function() {
        var day = parseInt(card.dataset.day, 10);
        if (progress.completed.indexOf(day) !== -1) return; // already done

        // LOGIN CHECK: Day 2+ requires login (unless preview mode)
        if (!isPreview && day > 1 && !isLoggedIn) {
          showToast(strings.loginRequired || 'Log in to continue from Day 2');
          // Redirect to login after brief delay
          setTimeout(function() { window.location.href = loginUrl; }, 1500);
          return;
        }

        // SEQUENTIAL CHECK: must complete previous day (unless preview mode)
        if (!isPreview && day > 1 && progress.completed.indexOf(day - 1) === -1) {
          showToast(strings.completePrevious || 'Complete the previous day first');
          return;
        }

        // Populate modal
        var iconEl = document.querySelector('[data-day-modal-icon]');
        var titleEl = document.querySelector('[data-day-modal-title]');
        var msgEl = document.querySelector('[data-day-modal-msg]');
        var tipEl = document.querySelector('[data-day-modal-tip]');

        if (iconEl) iconEl.innerHTML = dayIcons[day] || '';
        if (titleEl) titleEl.textContent = card.dataset.title || ('Day ' + day);
        if (msgEl) msgEl.textContent = card.dataset.msg || 'Well done!';
        if (tipEl) tipEl.textContent = card.dataset.tip || '';

        openModal(modal);

        // Save
        progress.completed.push(day);
        saveProgress();
        updateUI();

        // Day 10 → spin wheel
        if (day === 10 && spinModal) {
          setTimeout(function() {
            closeModal(modal);
            setTimeout(function() { initAndOpenSpin(false); }, 400);
          }, 2500);
        }
      });
    });

    // Modal close
    on(modalClose, 'click', function() { closeModal(modal); });
    on(modal, 'click', function(e) { if (e.target === modal) closeModal(modal); });
    on(document, 'keydown', function(e) {
      if (e.key === 'Escape') { closeModal(modal); closeModal(spinModal); }
    });

    // Reset button
    var resetBtn = section.querySelector('[data-challenge-reset]');
    on(resetBtn, 'click', function() {
      progress = { completed: [] };
      localStorage.removeItem('elora_challenge');
      localStorage.removeItem('elora_wheel_spun');
      updateUI();
      showToast(strings.resetDone || 'Progress reset!');
    });

    // Preview Spin button
    var testSpinBtn = section.querySelector('[data-challenge-test-spin]');
    on(testSpinBtn, 'click', function() {
      localStorage.removeItem('elora_wheel_spun');
      initAndOpenSpin(true);
    });
  }

  /* ─── Spin Wheel ─── */
  function initAndOpenSpin(isTest) {
    var modal = document.querySelector('[data-spin-modal]');
    if (!modal) return;
    var strings = window.ELORA_CHALLENGE?.strings || {};

    if (!isTest && localStorage.getItem('elora_wheel_spun')) {
      showToast(strings.alreadyClaimed || 'You already claimed your reward!');
      return;
    }

    modal.style.opacity = '1'; modal.style.visibility = 'visible';
    document.body.style.overflow = 'hidden';

    var canvas = document.getElementById('spin-wheel');
    var spinBtn = modal.querySelector('[data-spin-btn]');
    var resultEl = modal.querySelector('[data-spin-result]');
    var closeBtn = modal.querySelector('[data-spin-modal-close]');
    if (!canvas || !spinBtn) return;

    // Reset UI
    spinBtn.style.display = ''; spinBtn.disabled = false; spinBtn.style.opacity = '1';
    if (resultEl) { resultEl.style.display = 'none'; resultEl.innerHTML = ''; }

    var ctx = canvas.getContext('2d');
    var size = Math.min(canvas.parentElement.offsetWidth - 40, 280);
    canvas.width = size; canvas.height = size;

    var cfgPrizes = window.ELORA_CHALLENGE?.prizes || [];
    var colors = ['#C4A882', '#A99076', '#C9A96E', '#8C8078', '#D4B896', '#B8976A'];
    var prizes = cfgPrizes.length > 0
      ? cfgPrizes.map(function(p, i) { return { label: p.label, code: p.code, color: colors[i % colors.length] }; })
      : [
          { label: '$10 OFF', code: 'ELORA10', color: '#C4A882' },
          { label: '$15 OFF', code: 'ELORA15', color: '#C9A96E' },
          { label: '$20 OFF', code: 'ELORA20', color: '#D4B896' }
        ];

    var n = prizes.length;
    var arc = (2 * Math.PI) / n;
    var angle = 0;
    var spinning = false;

    function draw() {
      var cx = size / 2, cy = size / 2, r = size / 2 - 6;
      ctx.clearRect(0, 0, size, size);
      for (var i = 0; i < n; i++) {
        var sa = angle + i * arc, ea = sa + arc;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.arc(cx, cy, r, sa, ea); ctx.closePath();
        ctx.fillStyle = prizes[i].color; ctx.fill();
        ctx.strokeStyle = '#FDFBF8'; ctx.lineWidth = 2; ctx.stroke();
        ctx.save(); ctx.translate(cx, cy); ctx.rotate(sa + arc / 2);
        ctx.fillStyle = '#FDFBF8'; ctx.font = 'bold ' + Math.round(size / 26) + 'px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(prizes[i].label, r * 0.6, 0); ctx.restore();
      }
      ctx.beginPath(); ctx.arc(cx, cy, 14, 0, Math.PI * 2);
      ctx.fillStyle = '#FDFBF8'; ctx.fill();
      ctx.strokeStyle = '#C4A882'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(size - 2, cy - 9); ctx.lineTo(size - 2, cy + 9);
      ctx.lineTo(size - 20, cy); ctx.closePath(); ctx.fillStyle = '#2C2C2C'; ctx.fill();
    }
    draw();

    function doSpin() {
      if (spinning) return;
      spinning = true; spinBtn.disabled = true; spinBtn.style.opacity = '0.5';
      var winIdx = Math.floor(Math.random() * n);
      var target = Math.PI * 10 + (n - winIdx - 0.5) * arc;
      var start = performance.now();
      var dur = 4200;
      function anim(now) {
        var t = Math.min((now - start) / dur, 1);
        angle = (1 - Math.pow(1 - t, 3)) * target;
        draw();
        if (t < 1) { requestAnimationFrame(anim); }
        else {
          spinning = false;
          if (!isTest) localStorage.setItem('elora_wheel_spun', '1');
          var prize = prizes[winIdx];
          if (resultEl) {
            resultEl.innerHTML =
              '<div style="font-family:var(--font-heading);font-size:20px;margin-bottom:6px;color:var(--color-text);font-weight:500;">' + (strings.youWon || 'You won') + ' ' + prize.label + '!</div>' +
              (prize.code
                ? '<div style="font-size:13px;color:var(--color-muted);">' + (strings.useCode || 'Use code') + ': <strong style="color:var(--color-primary);letter-spacing:0.06em;">' + prize.code + '</strong></div>'
                : '') +
              '<div style="font-size:12px;color:var(--color-muted);margin-top:6px;">' + (strings.spinValid || 'Valid on orders over $100') + '</div>' +
              (isTest ? '<div style="font-size:11px;color:var(--color-gold);margin-top:8px;font-style:italic;">' + (strings.previewNote || '(Preview — not saved)') + '</div>' : '');
            resultEl.style.display = 'block';
          }
          spinBtn.style.display = 'none';
          showToast((strings.youWon || 'You won') + ' ' + prize.label + '!', 4000);
        }
      }
      requestAnimationFrame(anim);
    }

    // Replace button to avoid stacked listeners
    var newBtn = spinBtn.cloneNode(true);
    spinBtn.parentNode.replaceChild(newBtn, spinBtn);
    on(newBtn, 'click', doSpin);

    function closeSpin() {
      modal.style.opacity = '0'; modal.style.visibility = 'hidden';
      document.body.style.overflow = '';
    }
    on(closeBtn, 'click', closeSpin);
    on(modal, 'click', function(e) { if (e.target === modal) closeSpin(); });
  }

  /* ─── Bespoke Kit Builder ─── */
  function initKitBuilder() {
    var section = document.querySelector('[data-kit-builder]');
    if (!section) return;

    var productCards = section.querySelectorAll('[data-kit-item]');
    var summaryList = section.querySelector('[data-kit-summary]');
    var subtotalEl = section.querySelector('[data-kit-subtotal]');
    var addAllBtn = section.querySelector('[data-kit-add-all]');
    var emptyEl = section.querySelector('[data-kit-empty]');
    var discountLineEl = section.querySelector('[data-kit-discount-line]');
    var discountAmountEl = section.querySelector('[data-kit-discount-amount]');

    var kitCfg = window.ELORA_KIT || {};
    var strings = kitCfg.strings || {};
    var minItems = kitCfg.minItems || 4;
    var maxItems = kitCfg.maxItems || 8;
    var discountPct = kitCfg.discountPercent || 10;

    // selected = [{ handle, variantId, price, title, colorName }]
    var selected = [];

    function findByHandle(handle) {
      for (var i = 0; i < selected.length; i++) {
        if (selected[i].handle === handle) return i;
      }
      return -1;
    }

    function updateSummary() {
      var count = selected.length;

      // Empty state
      if (emptyEl) emptyEl.style.display = count === 0 ? 'block' : 'none';

      // Summary list with remove buttons
      if (summaryList) {
        summaryList.innerHTML = selected.map(function(s, idx) {
          return '<li style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;font-size:13px;border-bottom:1px solid var(--color-light);">' +
            '<div style="flex:1;min-width:0;">' +
              '<div style="font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + s.title + '</div>' +
              (s.colorName ? '<div style="font-size:11px;color:var(--color-muted);">' + s.colorName + '</div>' : '') +
            '</div>' +
            '<div style="display:flex;align-items:center;gap:8px;margin-left:8px;">' +
              '<span style="color:var(--color-muted);white-space:nowrap;">' + formatMoney(s.price) + '</span>' +
              '<button data-kit-remove="' + idx + '" style="background:none;border:none;color:var(--color-muted);cursor:pointer;font-size:16px;padding:0 2px;line-height:1;" title="' + (strings.remove || 'Remove') + '">&times;</button>' +
            '</div></li>';
        }).join('');

        // Wire remove buttons
        summaryList.querySelectorAll('[data-kit-remove]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var idx = parseInt(btn.dataset.kitRemove, 10);
            if (idx >= 0 && idx < selected.length) {
              var handle = selected[idx].handle;
              selected.splice(idx, 1);
              // Deselect the card
              productCards.forEach(function(card) {
                if (card.dataset.productHandle === handle) {
                  deselectCard(card);
                }
              });
              updateSummary();
            }
          });
        });
      }

      // Discount line
      if (discountLineEl) {
        if (count >= minItems) {
          var original = selected.reduce(function(sum, s) { return sum + s.price; }, 0);
          var discount = Math.round(original * discountPct / 100);
          if (discountAmountEl) discountAmountEl.textContent = '-' + formatMoney(discount);
          discountLineEl.style.display = 'block';
        } else {
          discountLineEl.style.display = 'none';
        }
      }

      // Subtotal
      if (subtotalEl) {
        var total = selected.reduce(function(sum, s) { return sum + s.price; }, 0);
        if (count >= minItems) {
          var disc = Math.round(total * discountPct / 100);
          subtotalEl.innerHTML = '<span style="text-decoration:line-through;color:var(--color-muted);margin-right:8px;font-size:14px;">' + formatMoney(total) + '</span><span style="color:var(--color-primary);font-weight:600;">' + formatMoney(total - disc) + '</span>';
        } else {
          subtotalEl.textContent = count > 0 ? formatMoney(total) : '$0';
        }
      }

      // Button state
      if (addAllBtn) {
        addAllBtn.disabled = count < minItems;
        addAllBtn.style.opacity = count < minItems ? '0.5' : '1';
      }
    }

    function deselectCard(card) {
      card.classList.remove('is-selected');
      // Reset swatches
      card.querySelectorAll('[data-kit-swatch]').forEach(function(s) {
        s.classList.remove('is-active');
      });
      var label = card.querySelector('[data-kit-color-label]');
      if (label) label.textContent = '';
    }

    function selectCard(card, variantId, price, title, colorName) {
      card.classList.add('is-selected');
      var label = card.querySelector('[data-kit-color-label]');
      if (label && colorName) label.textContent = colorName;
    }

    // ─── Setup each product card ───
    productCards.forEach(function(card) {
      var handle = card.dataset.productHandle;
      if (!handle) { card.style.opacity = '0.5'; card.style.cursor = 'default'; return; }

      // Get default variant from card data attributes (always present)
      var defaultVariantId = card.dataset.variantId || '';
      var defaultPrice = parseInt(card.dataset.variantPrice || '0', 10);
      var defaultTitle = card.dataset.productTitle || 'Product';

      // Swatches (if any)
      var swatches = card.querySelectorAll('[data-kit-swatch]');
      var variantSelect = card.querySelector('[data-kit-variant-select]');

      // Track current variant for this card
      var currentVariant = { id: defaultVariantId, price: defaultPrice, title: defaultTitle, color: '' };

      // ── Swatch click handler ──
      if (swatches.length > 0) {
        swatches.forEach(function(swatch) {
          swatch.addEventListener('click', function(e) {
            e.stopPropagation();
            if (swatch.disabled) return;

            var vId = swatch.dataset.variantId;
            var vPrice = parseInt(swatch.dataset.variantPrice || '0', 10);
            var vTitle = swatch.dataset.variantTitle || defaultTitle;
            var vColor = swatch.dataset.colorName || '';
            var vImage = swatch.dataset.variantImage;

            // If same swatch active → deselect product
            if (swatch.classList.contains('is-active')) {
              var idx = findByHandle(handle);
              if (idx > -1) selected.splice(idx, 1);
              deselectCard(card);
              updateSummary();
              return;
            }

            // Update current variant
            currentVariant = { id: vId, price: vPrice, title: vTitle, color: vColor };

            var idx = findByHandle(handle);
            if (idx > -1) {
              selected[idx] = { handle: handle, variantId: vId, price: vPrice, title: vTitle, colorName: vColor };
            } else {
              if (selected.length >= maxItems) { showToast(strings.maxReached || 'Max ' + maxItems + ' items'); return; }
              selected.push({ handle: handle, variantId: vId, price: vPrice, title: vTitle, colorName: vColor });
            }

            swatches.forEach(function(s) { s.classList.remove('is-active'); });
            swatch.classList.add('is-active');
            if (vImage) { var img = card.querySelector('[data-kit-img]'); if (img) img.src = vImage; }
            var priceEl = card.querySelector('[data-kit-price]');
            if (priceEl) priceEl.textContent = formatMoney(vPrice);
            selectCard(card, vId, vPrice, vTitle, vColor);
            updateSummary();
          });
        });
      }

      // ── Dropdown handler ──
      if (variantSelect) {
        variantSelect.addEventListener('click', function(e) { e.stopPropagation(); });
        variantSelect.addEventListener('change', function() {
          var opt = variantSelect.options[variantSelect.selectedIndex];
          if (!opt) return;
          currentVariant = {
            id: opt.value,
            price: parseInt(opt.dataset.price || '0', 10),
            title: opt.dataset.title || defaultTitle,
            color: ''
          };
          var idx = findByHandle(handle);
          if (idx > -1) {
            selected[idx].variantId = currentVariant.id;
            selected[idx].price = currentVariant.price;
            selected[idx].title = currentVariant.title;
            if (opt.dataset.image) { var img = card.querySelector('[data-kit-img]'); if (img) img.src = opt.dataset.image; }
            var priceEl = card.querySelector('[data-kit-price]');
            if (priceEl) priceEl.textContent = formatMoney(currentVariant.price);
            updateSummary();
          }
        });
      }

      // ── Card click (unified for ALL products) ──
      card.addEventListener('click', function(e) {
        if (e.target.closest('[data-kit-swatch]')) return;
        if (e.target.closest('[data-kit-variant-select]')) return;

        var idx = findByHandle(handle);
        if (idx > -1) {
          // Deselect
          selected.splice(idx, 1);
          deselectCard(card);
          updateSummary();
        } else {
          // Select — use current variant or auto-click first swatch
          if (swatches.length > 0 && !card.querySelector('.kit-swatch.is-active')) {
            var first = card.querySelector('[data-kit-swatch]:not([disabled])');
            if (first) { first.click(); return; }
          }

          // Use whatever variant we have
          var vId = currentVariant.id || defaultVariantId;
          var vPrice = currentVariant.price || defaultPrice;
          var vTitle = currentVariant.title || defaultTitle;
          if (!vId) return;

          if (selected.length >= maxItems) { showToast(strings.maxReached || 'Max ' + maxItems + ' items'); return; }
          selected.push({ handle: handle, variantId: vId, price: vPrice, title: vTitle, colorName: currentVariant.color || '' });
          selectCard(card, vId, vPrice, vTitle, currentVariant.color || '');
          updateSummary();
        }
      });
    });

    // ─── Add All to Cart ───
    on(addAllBtn, 'click', function() {
      if (selected.length < minItems) return;

      var origText = addAllBtn.textContent;
      addAllBtn.disabled = true;
      addAllBtn.textContent = '...';

      var itemsToAdd = selected.map(function(s) {
        return {
          id: parseInt(s.variantId, 10),
          quantity: 1
        };
      });

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var cartAddUrl = (window.ELORA && window.ELORA.routes && window.ELORA.routes.cart_add_url) || (root + 'cart/add');
      var cartUpdateUrl = root + 'cart/update.js';
      var discountCode = (kitCfg.discountCode || '').trim();

      fetch(cartAddUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: itemsToAdd })
      })
      .then(function(res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function() {
        if (!discountCode) return null;

        return fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            discount: discountCode
          })
        }).then(function(res) {
          if (!res.ok) throw new Error('Discount apply failed: ' + res.status);
          return res.json();
        });
      })
      .then(function() {
        showToast(strings.added || 'Kit added to cart!');
        if (typeof refreshCartDrawer === 'function') refreshCartDrawer();
        if (typeof window.openCartDrawer === 'function') window.openCartDrawer();

        selected = [];
        productCards.forEach(function(card) { deselectCard(card); });
        updateSummary();
      })
      .catch(function(e) {
        console.error('Kit add error', e);
        showToast('Error adding kit');
      })
      .finally(function() {
        addAllBtn.disabled = false;
        addAllBtn.textContent = origText;
      });
    });

    updateSummary();
  }

  /* ─── Product Page ─── */
  function initProductPage() {
    // ── Gallery Thumbnail Navigation ──
    const galleryMain = $('[data-gallery-main]');
    if (galleryMain) {
      $$('[data-gallery-thumb]').forEach(thumb => {
        on(thumb, 'click', () => {
          const src = thumb.dataset.src;
          if (!src) return;
          const mainImg = $('img', galleryMain);
          if (mainImg) {
            mainImg.src = src;
            mainImg.srcset = src + ' 900w';
          }
          $$('[data-gallery-thumb]').forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        });
      });
    }

    // ── Collapsible Sections ──
    $$('[data-collapsible-trigger]').forEach(trigger => {
      on(trigger, 'click', () => {
        const content = trigger.nextElementSibling || $('[data-collapsible-content]', trigger.parentElement);
        const isOpen = trigger.classList.contains('open');
        trigger.classList.toggle('open');
        const icon = $('.collapsible__icon', trigger);
        if (icon) icon.textContent = isOpen ? '+' : '−';
        trigger.setAttribute('aria-expanded', !isOpen);
        if (content) {
          content.classList.toggle('open');
          if (isOpen) {
            content.style.maxHeight = '0';
            content.style.paddingBottom = '0';
          } else {
            content.style.maxHeight = content.scrollHeight + 'px';
            content.style.paddingBottom = '20px';
          }
        }
      });
    });

    // ── Variant Selector (JSON-based) ──
    const variantsJson = $('#product-variants-json');
    let variants = [];
    if (variantsJson) {
      try { variants = JSON.parse(variantsJson.textContent); } catch (e) {}
    }

    $$('.product-variant-select').forEach(select => {
      on(select, 'change', () => {
        // Build selected options array
        const selects = $$('.product-variant-select');
        const selectedOptions = selects.map(s => s.value);

        // Find matching variant
        const variant = variants.find(v => {
          return v.options.every((opt, i) => opt === selectedOptions[i]);
        });

        if (!variant) return;

        const priceEl = $('.product-price');
        const comparePriceEl = $('.product-compare-price');
        const addBtn = $('.product-add-btn');
        const hiddenInput = $('input[name="id"]');
        const badge = $('.product-badge--sale');

        if (hiddenInput) hiddenInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (comparePriceEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            comparePriceEl.textContent = formatMoney(variant.compare_at_price);
            comparePriceEl.style.display = '';
            if (badge) badge.style.display = '';
          } else {
            comparePriceEl.style.display = 'none';
            if (badge) badge.style.display = 'none';
          }
        }
        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available
            ? (window.ELORA?.strings?.addToCart || 'Add to Cart')
            : (window.ELORA?.strings?.soldOut || 'Sold Out');
        }

        // Update gallery image if variant has an image
        if (variant.featured_image && galleryMain) {
          const mainImg = $('img', galleryMain);
          if (mainImg) {
            mainImg.src = variant.featured_image.src;
            mainImg.srcset = variant.featured_image.src + ' 900w';
          }
        }

        // Update URL without reload
        if (history.replaceState) {
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
      });
    });

    // ── Product Form Submit ──
    $$('[data-product-form]').forEach(form => {
      on(form, 'submit', async e => {
        e.preventDefault();
        const variantId = $('input[name="id"]', form)?.value || $('select[name="id"]', form)?.value;
        const qty = parseInt($('input[name="quantity"]', form)?.value || '1', 10);
        if (!variantId) return;
        await addToCart(parseInt(variantId, 10), qty);
      });
    });

    // ── Quantity Buttons ──
    $$('.qty-btn').forEach(btn => {
      on(btn, 'click', () => {
        const input = btn.parentElement.querySelector('input[name="quantity"]');
        if (!input) return;
        let val = parseInt(input.value, 10) || 1;
        if (btn.dataset.action === 'increase') val++;
        else val = Math.max(1, val - 1);
        input.value = val;
      });
    });
  }

  /* ─── Scroll Animations ─── */
  function initScrollAnimations() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    $$('[data-animate]').forEach(el => observer.observe(el));
  }

  /* ─── Collapsible Sections ─── */
  function initCollapsibles() {
    $$('.collapsible__trigger').forEach(trigger => {
      on(trigger, 'click', () => {
        const content = trigger.nextElementSibling;
        const isOpen = trigger.classList.contains('open');
        trigger.classList.toggle('open', !isOpen);
        trigger.setAttribute('aria-expanded', !isOpen);
        if (content) {
          content.style.maxHeight = isOpen ? '0' : content.scrollHeight + 'px';
        }
      });
    });
  }

  /* ─── Smooth Scroll for Anchor Links ─── */
  function initSmoothScroll() {
    // Handle hash on page load (e.g. coming from another page via /#challenge)
    if (window.location.hash) {
      var target = document.querySelector(window.location.hash);
      if (target) {
        setTimeout(function() {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    }

    // Close mobile menu when clicking anchor links
    var mobileMenu = document.querySelector('[data-mobile-menu]');
    document.querySelectorAll('a[href*="#"]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        var href = link.getAttribute('href');
        // Only handle anchors on the same page or homepage anchors
        if (href.indexOf('#') === -1) return;

        var hash = href.substring(href.indexOf('#'));
        var isHomepage = window.location.pathname === '/' || window.location.pathname === '';
        var isHashOnly = href.startsWith('#') || href.startsWith('/#');

        // Close mobile menu
        if (mobileMenu && mobileMenu.classList.contains('is-open')) {
          mobileMenu.classList.remove('is-open');
          document.body.style.overflow = '';
        }

        // If we're on homepage and it's a homepage anchor, scroll directly
        if (isHomepage && isHashOnly) {
          var el = document.querySelector(hash);
          if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            history.pushState(null, '', hash);
          }
        }
        // If we're NOT on homepage, let the browser navigate to /#section
      });
    });
  }

  /* ─── Initialize All ─── */
  function init() {
    initAnnouncement();
    initHero();
    initHeader();
    initCartDrawer();
    initQuickAdd();
    initSearch();
    initEmailPopup();
    initChallenge();
    initKitBuilder();
    initProductPage();
    initScrollAnimations();
    initCollapsibles();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
